import React, { useState, useEffect } from 'react';
import { Tabs, Tab } from '@material-ui/core';
import classNames from 'classnames';
import _ from 'lodash';

interface SimpleTabsProps {
  /**
   * Tabs array: <br/>
   *  - *label*: text shown in tab <br/>
   *  - *variant*: css variant for individual tab <br/>
   *  - *disabled*: tab disabled status
   */
  labels: { label: string, variant?: string, disabled?: boolean }[],
  /**
   * Class for BEM methodology.
   */
  containerClass?: string,
  /**
   * Bottom bar that indicates selected tab.
   */
  showIndicator?: boolean,
  /**
   * Determines additional display behavior of the tabs.
   */
  variant?: 'fullWidth' | 'scrollable' | 'standard',
  /**
   * If only 1 element is given, it will always show that, no matter the tab. Number of elements should match number of tabs.
   */
  children?: React.ReactNode,
  /**
   * Tab to be selected by default. If none provided, it will default to 0.
   */
  defaultTab?: number,
  /**
   * Optional onChange function in case the parent wants to trigger anything else when tabs change.
   *
   * *function(event: object, value: number) => void*
   */
  onChange?: Function
}

export default function SimpleTabs({
  labels,
  containerClass,
  children,
  onChange,
  showIndicator = true,
  variant = 'standard',
  defaultTab = 0
}: SimpleTabsProps) {
  const [tab, setTab] = useState(defaultTab);

  let count = -1;

  const childrenToRender = _.isArray(children) ? _.compact(children) : null;

  const onTabChange = (e, value: number) => {
    setTab(value);

    if (onChange) {
      onChange(e, value);
    }
  };

  useEffect(() => {
    setTab(defaultTab);
  }, [defaultTab]);

  return (
    <>
      <Tabs
        value={ tab }
        variant={ variant }
        { ...(containerClass && { className: `${ containerClass }__tabs` }) }
        onChange={ onTabChange }
        TabIndicatorProps={ showIndicator ? { className: `${ containerClass }__indicator` } : { style: { display: 'none' } } }
      >
        {
          _.map(labels, (item, index: number) => {
            if (item.disabled !== true) count++;

            return (
              <Tab
                key={ `${ item.label }_${ index }` }
                label={ item.label }
                value={ item.disabled !== true ? count : null }
                disabled={ item.disabled === true }
                {
                  ...(containerClass && {
                    className: classNames({
                      [`${ containerClass }__tab`]: true,
                      [`${ containerClass }__tab--selected`]: item.disabled !== true ? tab === count : false,
                      [`${ containerClass }__tab--${ item.variant }`]: !_.isUndefined(item.variant),
                      [`${ containerClass }__tab--disabled`]: item.disabled === true
                    })
                  })
                }
              />
            );
          })
        }
      </Tabs>

      { _.isArray(children) ? childrenToRender[tab] : children }
    </>
  );
}
